import { Card, Skeleton } from "@mui/material";
import { ReactNode } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { pulseSx, surfaceCardSx } from "@/components/screens/ServiceDashboard/components/styles";

export type BarListColumn = { header: string; align?: "left" | "right" | "center"; width?: string };

export type BarListRow = { key: string; cells: ReactNode[] };

type Props = {
  title: string;
  columns?: BarListColumn[];
  gridTemplateColumns: string;
  rows: BarListRow[];
  emptyLabel: string;
  isLoading?: boolean;
  isRefreshing?: boolean;
  /** Limits the rows area height and enables vertical scroll. */
  maxRowsHeight?: string;
};

export const ProgressBar = ({ percent, color }: { percent: number; color: string }) => (
  <MDBox
    width="100%"
    height="0.45rem"
    borderRadius={"1rem" as never}
    sx={(theme) => ({ backgroundColor: theme.palette.grey[200], overflow: "hidden" })}
  >
    <MDBox
      height="100%"
      borderRadius={"1rem" as never}
      sx={{
        width: `${Math.max(0, Math.min(100, percent))}%`,
        backgroundColor: color,
        transition: "width 900ms ease",
      }}
    />
  </MDBox>
);

const BarListCard = ({
  title,
  columns,
  gridTemplateColumns,
  rows,
  emptyLabel,
  isLoading,
  isRefreshing = false,
  maxRowsHeight,
}: Props) => {
  const justify = (align?: string) =>
    align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start";

  return (
    <Card sx={surfaceCardSx()}>
      <MDBox px={1.75} py={1.25}>
        <MDTypography variant="button" fontWeight="bold" display="block" mb={0.75}>
          {title}
        </MDTypography>
        {columns && (
          <MDBox display="grid" gap={1} mb={0.5} sx={{ gridTemplateColumns }}>
            {columns.map((column, index) => (
              <MDBox key={index} display="flex" justifyContent={justify(column.align)}>
                <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
                  {column.header}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        )}
        {isLoading ? (
          [0, 1, 2, 3].map((item) => <Skeleton key={item} variant="text" height={20} />)
        ) : rows.length === 0 ? (
          <MDTypography variant="caption" color="text">
            {emptyLabel}
          </MDTypography>
        ) : (
          <MDBox
            sx={{
              ...pulseSx(isRefreshing),
              ...(maxRowsHeight ? { maxHeight: maxRowsHeight, overflowY: "auto", pr: 0.5 } : {}),
            }}
          >
            {rows.map((row) => (
              <MDBox
                key={row.key}
                display="grid"
                gap={1}
                py={0.4}
                alignItems="center"
                sx={{ gridTemplateColumns }}
              >
                {row.cells.map((cell, index) => (
                  <MDBox
                    key={index}
                    display="flex"
                    alignItems="center"
                    justifyContent={justify(columns?.[index]?.align)}
                    minWidth={0}
                  >
                    {typeof cell === "string" || typeof cell === "number" ? (
                      <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        color="dark"
                        sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {cell}
                      </MDTypography>
                    ) : (
                      cell
                    )}
                  </MDBox>
                ))}
              </MDBox>
            ))}
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
};

export default BarListCard;
